import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	ArrowRight,
	Loader2,
	MessageSquare,
	SkipForward,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/hooks/use-session";
import { apiService } from "@/lib/api-service";
import {
	trackOnboardingFollowUpCompleted,
	trackOnboardingFollowUpSkipped,
} from "@/lib/analytics-events";

export const Route = createFileRoute(
	"/_app/_/onboarding/interview-follow-up",
)({
	component: InterviewFollowUpPage,
});

interface FollowUpQuestion {
	question_index: number;
	original_question: string;
	original_answer: string;
	flag_reason: string;
	flag_reason_display: string;
	follow_up_prompt: string;
	category: string;
}

function InterviewFollowUpPage() {
	const navigate = useNavigate();
	const session = useSession();
	const campaignSlug = session?.campaign?.slug;
	const [currentIdx, setCurrentIdx] = useState(0);
	const [answers, setAnswers] = useState<
		Map<number, { text: string; skipped: boolean }>
	>(new Map());
	const [inputValue, setInputValue] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["follow-up-questions", campaignSlug],
		queryFn: () => apiService.getFollowUpQuestions(campaignSlug!),
		enabled: !!campaignSlug,
	});

	const submitMutation = useMutation({
		mutationFn: (finalAnswers: Map<number, { text: string; skipped: boolean }>) => {
			const answersList = Array.from(finalAnswers.entries()).map(
				([questionIndex, { text, skipped }]) => ({
					question_index: questionIndex,
					answer_text: text,
					skipped,
				}),
			);
			return apiService.submitFollowUpAnswers(campaignSlug!, answersList);
		},
		onSuccess: (_data, submittedAnswers) => {
			const answeredCount = Array.from(submittedAnswers.values()).filter(
				(a) => !a.skipped,
			).length;
			trackOnboardingFollowUpCompleted(answeredCount);
			navigate({ to: "/onboarding/generating" });
		},
	});

	// Auto-redirect if 0 questions
	useEffect(() => {
		if (data && data.total_flagged === 0) {
			navigate({ to: "/onboarding/generating" });
		}
	}, [data, navigate]);

	const questions: FollowUpQuestion[] = data?.questions ?? [];
	const current = questions[currentIdx];
	const isLast = currentIdx === questions.length - 1;

	const advanceOrSubmit = useCallback(
		(updatedAnswers: Map<number, { text: string; skipped: boolean }>) => {
			if (isLast) {
				submitMutation.mutate(updatedAnswers);
			} else {
				setCurrentIdx((i) => i + 1);
				setInputValue("");
			}
		},
		[isLast, submitMutation],
	);

	const handleSubmit = useCallback(() => {
		if (!current) return;
		const updatedAnswers = new Map(answers);
		updatedAnswers.set(current.question_index, {
			text: inputValue,
			skipped: false,
		});
		setAnswers(updatedAnswers);
		advanceOrSubmit(updatedAnswers);
	}, [current, inputValue, answers, advanceOrSubmit]);

	const handleSkip = () => {
		if (!current) return;
		const updatedAnswers = new Map(answers);
		updatedAnswers.set(current.question_index, { text: "", skipped: true });
		setAnswers(updatedAnswers);
		advanceOrSubmit(updatedAnswers);
	};

	const handleSkipAll = () => {
		trackOnboardingFollowUpSkipped();
		navigate({ to: "/onboarding/generating" });
	};

	// Ctrl/Cmd + Enter to submit
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && inputValue.trim()) {
			handleSubmit();
		}
	};

	if (isLoading) {
		return (
			<div className="h-dvh flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (!current) return null;

	return (
		<div className="h-dvh flex flex-col items-center justify-center px-6">
			<div className="w-full max-w-lg space-y-6">
				{/* Progress */}
				<div className="space-y-2">
					<div className="flex justify-between text-sm text-muted-foreground">
						<span>Follow-up questions</span>
						<span>
							{currentIdx + 1} of {questions.length}
						</span>
					</div>
					<div className="h-2 rounded-full bg-muted">
						<div
							className="h-full rounded-full bg-primary transition-all"
							style={{
								width: `${((currentIdx + 1) / questions.length) * 100}%`,
							}}
						/>
					</div>
				</div>

				{/* Question Card */}
				<Card>
					<CardContent className="p-6 space-y-4">
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<MessageSquare className="h-4 w-4 text-primary" />
								<span className="text-sm font-medium text-primary capitalize">
									{current.category.replace(/_/g, " ")}
								</span>
							</div>
							<h2 className="text-lg font-semibold text-foreground">
								{current.original_question}
							</h2>
						</div>

						{/* Original answer */}
						{current.original_answer && (
							<div className="rounded-lg bg-muted/50 p-3">
								<p className="text-sm text-muted-foreground italic">
									Your previous answer: "{current.original_answer}"
								</p>
							</div>
						)}

						{/* Flag reason */}
						<div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
							<p className="text-sm text-amber-700 dark:text-amber-300">
								{current.flag_reason_display}
							</p>
						</div>

						{/* Follow-up prompt */}
						<p className="text-sm text-foreground font-medium">
							{current.follow_up_prompt}
						</p>

						{/* Input */}
						<Textarea
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder="Type your answer here..."
							rows={4}
							className="resize-none"
						/>

						{/* Actions */}
						<div className="flex items-center gap-3">
							<Button
								onClick={handleSubmit}
								disabled={!inputValue.trim() || submitMutation.isPending}
								className="flex-1"
							>
								{submitMutation.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin mr-2" />
								) : (
									<ArrowRight className="h-4 w-4 mr-2" />
								)}
								{isLast ? "Submit & Continue" : "Submit"}
							</Button>
							<Button variant="ghost" onClick={handleSkip}>
								<SkipForward className="h-4 w-4 mr-2" />
								Skip
							</Button>
						</div>

						<p className="text-xs text-muted-foreground text-center">
							Press Ctrl+Enter to submit
						</p>
					</CardContent>
				</Card>

				{/* Skip all */}
				<div className="text-center">
					<button
						type="button"
						onClick={handleSkipAll}
						className="text-sm text-muted-foreground hover:text-foreground underline"
					>
						Skip all and continue
					</button>
				</div>
			</div>
		</div>
	);
}
